import { useCallback } from 'react';
import { CONFIG } from '@frameio/config';
import { getDatadogRumSessionUrl } from '@frameio/telemetry/datadog';
import { useActiveAccountId } from '../contexts';
import { useCurrentPlanTracking } from '../plans/hooks/useCurrentPlanTracking';
import { useMeCachedQuery } from '.';

/**
 * The Next.js back-end route for submitting feedback. This approach ensures we
 * don't expose the Zapier webhook URL to the client.
 */
const FORM_SUBMISSION_URL = '/api/feedback';

type SubmitFeedbackInput = {
  /**
   * System metadata consumed by the feedback hub automations — must NOT be
   * localized.
   */
  origination: string;
  summary: string;
  description: string;
  /** Extra fields forwarded to the feedback hub alongside the standard ones. */
  additionalFormData?: Record<string, string>;
};

/**
 * Submits user feedback to the feedback hub with the standard app, account,
 * and user metadata attached. Resolves on success and throws on failure so
 * callers can drive their own submitting/toast states.
 */
export function useSubmitFeedback() {
  const accountId = useActiveAccountId();
  const currentPlanTier =
    useCurrentPlanTracking()?.current_plan_tier?.toUpperCase();
  const me = useMeCachedQuery();
  const userEmail = me?.data?.me?.email;
  const userName = me?.data?.me?.name;
  const userId = me?.data?.me?.id;

  return useCallback(
    async ({
      origination,
      summary,
      description,
      additionalFormData,
    }: SubmitFeedbackInput) => {
      const response = await fetch(FORM_SUBMISSION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId,
          appEnvironment: CONFIG.name?.split('_')?.[0],
          appVersion: CONFIG.releaseVersion,
          dataDogSessionUrl: getDatadogRumSessionUrl(),
          description,
          email: userEmail || 'Email',
          name: userName || 'Name',
          origination,
          pageUrl: window?.location.href,
          planTier: currentPlanTier,
          summary,
          userAgent: navigator?.userAgent,
          userId: userId || '',
          ...additionalFormData,
        }),
      });

      if (!response.ok) {
        throw new Error(`Feedback submission failed: ${response.status}`);
      }
    },
    [accountId, currentPlanTier, userEmail, userId, userName]
  );
}
